import { ViewPort } from "./View/ViewPort";
import { mat4, vec3 } from "gl-matrix";
import { Shader } from "./Shader";
import { Scene } from "./Scene";
import { TickEventManager } from "@spi/Common/Util/TickEventManager";
import { MaterialManager } from "./MaterialManager";
import { AssetLoader } from "./AssetLoader/AssetLoader";
import { TextureAsset } from "./AssetLoader/TextureAsset";
import { RenderObject } from "./Object/RenderObject";
import { InputMouseEvent } from "./Input/InputHandler";
import { unproject, Ray } from "@spi/Common/Math";
import { Mesh } from "./Object/Mesh";
import { MeshManager } from "./Object/MeshManager";
import { RenderObjectFactory } from "./Object/RenderObjectFactory";
import { ShaderManager } from "./Shader/";

export class Engine {
    public ctx: Context;
    private isActive: boolean;
    private pickingFrameBuffer: WebGLFramebuffer;

    public activeScene: Scene;
    public shaderManager: ShaderManager;
    public tickEventManager: TickEventManager;
    public materialManager: MaterialManager;
    public meshManager: MeshManager;
    public objectFactory: RenderObjectFactory;

    private lastMouseMoveEvent: InputMouseEvent;
    private currentHoverObject: RenderObject;

    constructor(public canvas: HTMLCanvasElement, public overlayLayer: HTMLDivElement, public viewPort: ViewPort, public config: EngineSettings) {
        this.tickEventManager = new TickEventManager();
        this.materialManager = new MaterialManager();

        this.activeScene = null;

        this.meshManager = new MeshManager();
        this.objectFactory = new RenderObjectFactory(this);
        
        this.lastMouseMoveEvent = null;
        this.currentHoverObject = null;

        this.tickEventManager.registerTimeEvent(() => {
            if (this.lastMouseMoveEvent) {
                this.objectHover(this.lastMouseMoveEvent);
            }
        }, 50);
    }

    public init(canvas: HTMLCanvasElement): Context {

        let gl = this.initGL(canvas, true);
        this.shaderManager = new ShaderManager(gl);

        let shaders = new Map<string, Shader>();

        for (let i in this.config.shaders) {

            for (let j in this.config.shaders[i]) {
                let config = this.config.shaders[i][j];
                let shader = new Shader(gl, config.fragment, config.vertex, []);
                shaders.set(`${i}.${j}`, shader);
            }
        }



        let ctx = new Context(this, gl, shaders, mat4.create(), mat4.create(), false);

        this.pickingFrameBuffer = this.initPicking(ctx);

        gl.clearColor(0.0, 0.0, 0.0, 1.0);
        gl.enable(gl.DEPTH_TEST);

        return ctx;
    }

    public activateScene(scene: Scene) {
        if (!scene) throw Error('scene must be set');

        if (this.activeScene) {
            this.activeScene.detached();
        }

        this.activeScene = scene;

        this.ctx.viewPort = scene.viewPort;
        scene.attached();
    }

    public halt() {
        this.isActive = false;
    }

    public async start(scene: Scene) {

        this.isActive = true;
        this.ctx = this.init(this.canvas);

        await this.loadAssets();
        this.activateScene(scene);
        this.renderLoop();
    }

    private renderLoop() {

        if (!this.isActive) {
            return;
        }

        this.tickEventManager.tick();
        this.activeScene.tickEventManager.tick();
        this.renderScene(this.ctx);
        requestAnimationFrame(() => this.renderLoop());
    }

    private renderPickingScene(ctx: Context, x: number, y: number): number {
        let gl = ctx.gl;

        let viewPort = ctx.viewPort;

        // Bind the picking buffer so everything gets drawn to that instead of the canvas
        gl.bindFramebuffer(gl.FRAMEBUFFER, this.pickingFrameBuffer);

        // Setup the viewport and clear the screen
        gl.viewport(0, 0, viewPort.width, viewPort.height);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // Apply all transformation exactly like we do for regular drawing
        viewPort.applyPerspective(ctx.projectionMatrix);
        mat4.identity(ctx.modelViewMatrix);

        viewPort.applyModelView(ctx.modelViewMatrix);

        // Draw all pickable objects
        this.activeScene.objectManager.iterate('picking', (obj) => {
            obj.draw(ctx, true);
        });

        // Have this function return the picked color
        let colorPicked = new Uint8Array(4);
        gl.readPixels(x, y, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, colorPicked);
        let color = colorPicked[0] | (colorPicked[1] << 8) | (colorPicked[2] << 16);

        return color;
    }

    private renderScene(ctx: Context): void {
        let gl = ctx.gl;

        let viewPort = ctx.viewPort;

        gl.bindFramebuffer(gl.FRAMEBUFFER, null); // make sure we draw to canvas

        gl.viewport(0, 0, viewPort.width, viewPort.height);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        viewPort.applyPerspective(ctx.projectionMatrix);
        mat4.identity(ctx.modelViewMatrix);

        viewPort.applyModelView(ctx.modelViewMatrix);

        for (let i = 0; i < this.activeScene.lightManager.lights.length; i++) {
            let light = this.activeScene.lightManager.lights[i];
            if (light.animatedProperties) {
                light.animatedProperties.step();
            }
        }

        this.activeScene.objectManager.iterate('main', (obj) => {
            if (obj.animatedProperties) {
                obj.animatedProperties.step();
            }
            obj.draw(ctx, false);
        });

        this.activeScene.textManager.iterate((text) => {
            text.draw(ctx);
        });

    }

    private initGL(canvas: HTMLCanvasElement, buffer: boolean): WebGLRenderingContext {
        try {
            let gl = canvas.getContext("webgl", { preserveDrawingBuffer: buffer }) || canvas.getContext("experimental-webgl", { preserveDrawingBuffer: buffer }); // For IE and older Firefox / Webkit browsers
            canvas.width = this.viewPort.width;
            canvas.height = this.viewPort.height;

            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            gl.enable(gl.BLEND);

            gl.enable(gl.CULL_FACE);
            gl.cullFace(gl.BACK);

            let requiredExtensions = ['OES_texture_float'];

            for (let extensionName of requiredExtensions) {
                let ext = gl.getExtension(extensionName as any);

                if (!ext) {
                    console.error(`Required extension ${extensionName} is not available.`);
                }
            }

            return gl;

        } catch (e) {
            console.error(e);
        }

        throw new Error("Could not initialize WebGL, the browser probably doesn't support it");
    }

    private initPicking(ctx: Context): WebGLFramebuffer {
        // https://stackoverflow.com/questions/29578535/webgl-binding-of-a-framebuffer-and-renderbuffer?rq=1
        // https://stackoverflow.com/questions/21841483/webgl-using-framebuffers-for-picking-multiple-objects

        let gl = ctx.gl;
        let width = ctx.viewPort.width;
        let height = ctx.viewPort.height;

        let framebuffer = gl.createFramebuffer();
        gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

        let depthBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);

        // allocate renderbuffer
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT16, width, height);

        // attach renderebuffer
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthBuffer);

        let colorBuffer = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, colorBuffer);
        // allocate colorBuffer
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.RGBA4, width, height);

        // attach colorbuffer
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, colorBuffer);

        if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
            console.error("this combination of attachments does not work");
        }

        return framebuffer;
    }

    private loadAssets(): Promise<void[]> {
        var self = this;

        var assetLoader = new AssetLoader();

        for (var i in this.config.assets.textures) {
            assetLoader.addAsset(new TextureAsset(i, this.config.assets.textures[i].source, false));
        }

        // TODO: Add other fixed assets
        return assetLoader.resolveAssets(self);

    }

    public objectHover(e: InputMouseEvent) {

        let x = e.position.x;
        let y = e.position.y;

        let ctx = this.ctx;

        let color = this.renderPickingScene(ctx, x, y);

        let obj = this.activeScene.pickingManager.getObject<RenderObject>(color);

        if (obj && obj.pickable) {
            obj.hovered = true;

            obj.interaction.onHoverIn();
        } else {
            let hitMap = this.activeScene.pickingManager.getRayIntersections(this.getRay(e));
            hitMap.forEach((value, key) => {
                value.callback(key, 'mouseMove', value.hits);
            });
        }

        if (this.currentHoverObject && this.currentHoverObject !== obj) {
            this.currentHoverObject.hovered = false;

            this.currentHoverObject.interaction.onHoverOut();
        }
        this.currentHoverObject = obj;

    }

    private getRay(e: InputMouseEvent): Ray {
        let ctx = this.ctx;

        let mvMatrix = mat4.create();
        ctx.viewPort.applyModelView(mvMatrix);

        let near = unproject(
            vec3.fromValues(e.position.x, e.position.y, 0),
            ctx.projectionMatrix,
            mvMatrix,
            ctx.viewPort);

        let far = unproject(
            vec3.fromValues(e.position.x, e.position.y, 1),
            ctx.projectionMatrix,
            mvMatrix,
            ctx.viewPort);

        return new Ray(
            vec3.fromValues(near[0], near[1], near[2]),
            vec3.fromValues(far[0], far[1], far[2])
        );
    }

    public mouseMoveEvent(e: InputMouseEvent) {
        this.lastMouseMoveEvent = e;
        this.objectHover(e);
    }

    public mouseUpEvent(e: InputMouseEvent) {
        var ctx = this.ctx;

        var color = this.renderPickingScene(ctx, e.position.x, e.position.y);

        var obj = this.activeScene.pickingManager.getObject<RenderObject>(color);

        if (obj && obj.pickable) {
            obj.interaction.onPressRelease();
        } else {
            var hitMap = this.activeScene.pickingManager.getRayIntersections(this.getRay(e));
            var done = false;

            hitMap.forEach((value, key) => {
                if (!done) { // TODO: This feels dirty, is there a different way to loop over Sets/Maps?
                    done = value.callback(key, 'mouseUp', value.hits);
                }
            });
        }

    }

    public mouseDownEvent(e: InputMouseEvent) {

        var ctx = this.ctx;

        var color = this.renderPickingScene(ctx, e.position.x, e.position.y);

        var obj = this.activeScene.pickingManager.getObject<RenderObject>(color);

        if (obj && obj.pickable) {
            obj.selected = !obj.selected;

            obj.interaction.onPressDown();
        } else {
            let hitMap = this.activeScene.pickingManager.getRayIntersections(this.getRay(e));
            hitMap.forEach((value, key) => {
                value.callback(key, 'mouseDown', value.hits);
            });
        }

    }

    public createObject(mesh: Mesh, callback?: (obj: RenderObject) => void): RenderObject {
        let obj = this.objectFactory.instantiateMesh(mesh);

        if (callback) {
            callback(obj);
        }

        return obj;
    }

    public createColladaObject(objectName: string, callback?: (obj: RenderObject) => void): RenderObject {
        
        let obj = this.objectFactory.create(objectName);

        if (callback) {
            callback(obj);
        }

        return obj;
    }
}

export class Context {

    public engine: Engine;
    public viewPort: ViewPort;
    public gl: WebGLRenderingContext;
    public projectionMatrix: mat4;
    public modelViewMatrix: mat4;
    public picking: boolean;
    public shaders: Map<string, Shader>;
    public currentShader: Shader;

    constructor(engine: Engine, gl: WebGLRenderingContext, shaders: Map<string, Shader>, pMatrix: mat4, mvMatrix: mat4, picking: boolean) {

        this.engine = engine;
        this.viewPort = engine.viewPort;
        this.gl = gl;
        this.shaders = shaders;
        this.projectionMatrix = pMatrix;
        this.modelViewMatrix = mvMatrix;
        this.picking = picking;
        this.shaders = shaders;
        this.currentShader = null;
    }

    // TODO: remove this temporary hack
    public setShader2(shader: any) {
        if (this.currentShader != shader) {
            if (this.currentShader) {
                this.currentShader.disableAttributes();
            }

            this.currentShader = shader;
            this.gl.useProgram(this.currentShader.program);

            this.currentShader.enableAttributes();
        }
        return this.currentShader;
    }

    public setShader(shaderReference: string) : Shader {
        if (!this.shaders.has(shaderReference)) {
            throw new Error(`Unknown shader set: ${shaderReference}`);
        }

        let shader = this.shaders.get(shaderReference);

        if (this.currentShader != shader) {
            if (this.currentShader) {
                this.currentShader.disableAttributes();
            }

            this.currentShader = shader;
            this.gl.useProgram(this.currentShader.program);

            this.currentShader.enableAttributes();
        }
        return this.currentShader;
    }
}


export type EngineSettings = {
    viewport: {
        width: number,
        height: number
    },
    keymap: {
        [key: string]: number[]
    },
    shaders: {
        [key: string]: {
            [key: string]: {
                vertex: string,
                fragment: string
            }
        }
    },
    assets: {
        meshes: any[],
        textures: {
            [key: string]: {
                source: string
            }
        }
    }
}

